import React, { useEffect, useState } from 'react';
import NextLink from 'next/link';
import {
  Block,
  Click,
  Col,
  Flex,
  Grid,
  Logo,
  Nav,
  NavItem,
  Row,
  Spacer,
  Text,
  Theme,
  ThemePicker,
  useTheme,
} from 'vcc-ui';
import packageJson from 'vcc-ui/package.json';

const NavItemContainer = (props) => {
  const theme = useTheme();
  return (
    <Flex
      extend={{
        justifyContent: 'center',
        alignItems: 'center',
        [theme.breakpoints.fromL]: {
          flexDirection: 'row',
        },
      }}
      {...props}
    />
  );
};

const LogoContainer = () => {
  return (
    <NextLink passHref href="/">
      <Block
        as="a"
        extend={{
          padding: '10px',
          position: 'absolute',
          left: '50%',
          top: '38px',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Logo height={10} alt="Volvo Cars" type="spreadmark" />
      </Block>
    </NextLink>
  );
};

const SmallViewportHeader = ({ onToggle, isOpen }) => {
  const theme = useTheme();
  return (
    <Flex
      extend={{
        alignItems: 'stretch',
        flexDirection: 'row',
        height: '73px',
        justifyContent: 'space-between',
        [theme.breakpoints.fromL]: {
          display: 'none',
        },
      }}
    >
      <NavItemContainer onClick={onToggle}>
        <Click>
          <Text variant="amundsen">{isOpen ? 'Close' : 'Menu'}</Text>
        </Click>
      </NavItemContainer>
      <LogoContainer />
    </Flex>
  );
};

const enableScrollLock = () => {
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.documentElement.style.overflow = 'hidden';
};

const disableScrollLock = (scrollPosition) => {
  document.body.style.position = 'static';
  document.documentElement.style.overflow = 'visible';
  window.scrollTo(0, scrollPosition);
};

export const Header = ({ activePage = 'start', hideOnScroll = true }) => {
  const [isOpen, setOpen] = useState(false);
  const [prevScrollY, setPrevScrollY] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setPrevScrollY(window.pageYOffset);
      enableScrollLock();
    } else {
      disableScrollLock(prevScrollY);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ThemePicker variant="dark">
      <Theme>
        {(theme) => (
          <header>
            <Nav sticky hideOnScroll={hideOnScroll} role="header">
              <Grid>
                <Row>
                  <Col>
                    <SmallViewportHeader
                      onToggle={() => setOpen(!isOpen)}
                      isOpen={isOpen}
                    />
                    <Flex
                      extend={{
                        display: isOpen ? 'flex' : 'none',
                        alignItems: 'stretch',
                        boxSizing: 'border-box',
                        minHeight: '100vh',
                        [theme.breakpoints.fromL]: {
                          display: 'flex',
                          minHeight: 'auto',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        },
                      }}
                    >
                      <NavItemContainer>
                        <NextLink href="/" passHref>
                          <NavItem href="/" isActive={activePage === 'start'}>
                            VCC UI
                          </NavItem>
                        </NextLink>
                        <NextLink href="/docs" passHref>
                          <NavItem
                            href="/docs"
                            isActive={activePage === 'docs'}
                          >
                            Docs
                          </NavItem>
                        </NextLink>
                        <NextLink href="/blog" passHref>
                          <NavItem
                            href="/blog"
                            isActive={activePage === 'blog'}
                          >
                            Blog
                          </NavItem>
                        </NextLink>
                      </NavItemContainer>
                      <Block
                        extend={{
                          display: 'none',
                          [theme.breakpoints.fromL]: { display: 'block' },
                        }}
                      >
                        <LogoContainer />
                      </Block>
                      <NavItemContainer>
                        <NavItem href="https://vcc-ui-storybook.vercel.app">
                          Storybook
                        </NavItem>
                        <NavItem href="https://vcc-ui-playroom.vercel.app">
                          Playroom
                        </NavItem>
                        <NavItem href="https://github.com/volvo-cars/vcc-ui/releases">
                          v{packageJson.version}
                        </NavItem>
                        <NavItem href="https:///github.com/volvo-cars/vcc-ui">
                          <Block
                            extend={{
                              fill:
                                theme.name === 'volvo-dark' ? '#fff' : '#000',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Block>Source</Block>
                            <Spacer />
                            <svg
                              height="20"
                              width="20"
                              role="img"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <title>GitHub Icon</title>
                              <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                            </svg>
                          </Block>
                        </NavItem>
                      </NavItemContainer>
                    </Flex>
                  </Col>
                </Row>
              </Grid>
            </Nav>
          </header>
        )}
      </Theme>
    </ThemePicker>
  );
};
